import React, { useEffect, useState, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/common/RichTextEditor";
import MediaImageField from "@/components/common/MediaImageField";
import { CRUD } from "@/constants/testIds";
import api, { API_URL } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic CRUD drawer. `fields` is an array of:
 *   { key, label, type: 'text'|'textarea'|'select'|'number'|'switch'|'tags', options?: [{label, value}] }
 */
export default function CrudDrawer({ open, onOpenChange, title, fields, initial, onSave, resourceKey }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState("{}");
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    const next = initial || {};
    setValues(next);
    setBaseline(JSON.stringify(next));
  }, [initial, open]);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const dirty = JSON.stringify(values) !== baseline;

  const requestClose = (nextOpen) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }
    if (dirty && !saving) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(values);
      setBaseline(JSON.stringify(values));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) submit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, values]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sheet open={open} onOpenChange={requestClose}>
      <SheetContent
        data-testid={CRUD.drawer(resourceKey)}
        className="w-full sm:max-w-lg glass-strong border-white/10 text-white p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-2 text-left">
          <SheetTitle className="text-white">{title}</SheetTitle>
          <SheetDescription className="text-white/50">
            {dirty ? "Unsaved changes · Ctrl/⌘S to save" : "Fill in the details and save."}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={f.key} className="text-xs uppercase tracking-wider text-white/50">{f.label}</Label>
              {f.type === "richtext" ? (
                <RichTextEditor
                  value={values[f.key] ?? ""}
                  onChange={(html) => set(f.key, html)}
                  placeholder={f.placeholder || "Start writing…"}
                  testId={`${resourceKey}-${f.key}`}
                />
              ) : f.type === "textarea" ? (
                <Textarea
                  id={f.key}
                  value={values[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={f.rows || 4}
                  className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
                />
              ) : f.type === "select" ? (
                <Select value={values[f.key] ?? ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger className="bg-black/20 border-white/10 text-white">
                    <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent className="glass-strong border-white/10">
                    {(f.options || []).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "switch" ? (
                <div className="flex items-center gap-3">
                  <Switch
                    id={f.key}
                    checked={!!values[f.key]}
                    onCheckedChange={(v) => set(f.key, v)}
                  />
                  <span className="text-sm text-white/60">{values[f.key] ? "Enabled" : "Disabled"}</span>
                </div>
              ) : f.type === "tags" ? (
                <Input
                  id={f.key}
                  value={Array.isArray(values[f.key]) ? values[f.key].join(", ") : (values[f.key] ?? "")}
                  onChange={(e) => set(f.key, e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                  placeholder="Comma-separated"
                  className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
                />
              ) : f.type === "number" ? (
                <Input
                  id={f.key}
                  type="number"
                  value={values[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                  className="bg-black/20 border-white/10 text-white"
                />
              ) : f.type === "image" ? (
                <MediaImageField
                  value={values[f.key] ?? (f.valueMode === "media" ? null : "")}
                  onChange={(url) => set(f.key, url)}
                  folder={f.folder || "uploads"}
                  valueMode={f.valueMode || "url"}
                  hint={f.hint}
                />
              ) : f.type === "gallery" ? (
                <GalleryUploadField
                  value={values[f.key] ?? []}
                  onChange={(urls) => set(f.key, urls)}
                  folder={f.folder || "blog"}
                />
              ) : f.type === "sections" ? (
                <SectionsEditor
                  value={values[f.key] ?? []}
                  onChange={(sections) => set(f.key, sections)}
                  folder={f.folder || "blog"}
                />
              ) : (
                <Input
                  id={f.key}
                  value={values[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="bg-black/20 border-white/10 text-white placeholder:text-white/30"
                />
              )}
              {f.hint && <p className="text-[11px] text-white/40">{f.hint}</p>}
            </div>
          )          )}
        </div>
        <div className="shrink-0 px-6 py-4 border-t border-white/[0.06] flex items-center justify-between gap-2 glass-strong">
          <span className={`text-[11px] ${dirty ? "text-amber-300" : "text-white/35"}`}>{dirty ? "Unsaved" : "Saved"}</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => requestClose(false)} className="text-white/70 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button
              data-testid={CRUD.saveBtn(resourceKey)}
              onClick={submit}
              disabled={saving}
              className="btn-gradient text-white border-0"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </SheetContent>
      {discardOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="glass-strong rounded-xl border border-white/10 p-5 max-w-sm w-full">
            <p className="text-white font-medium">Discard unsaved changes?</p>
            <p className="text-sm text-white/55 mt-1">Your edits in this drawer will be lost.</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setDiscardOpen(false)} className="text-white/70 hover:text-white">Keep editing</Button>
              <Button onClick={() => { setDiscardOpen(false); onOpenChange(false); }} className="bg-red-600 hover:bg-red-500 text-white border-0">Discard</Button>
            </div>
          </div>
        </div>
      )}
    </Sheet>
  );
}


function GalleryUploadField({ value = [], onChange, folder = "blog" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", folder);
        const { data } = await api.post("/media/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(`${API_URL}/api/media/public/${data.id}`);
      }
      onChange([...value, ...newUrls]);
      toast.success(`${newUrls.length} image(s) uploaded`);
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (e) => {
    upload(e.target.files);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    const updated = value.filter((_, i) => i !== idx);
    onChange(updated);
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    onChange(arr);
  };

  return (
    <div className="space-y-3">
      {/* Preview grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((url, idx) => (
            <div key={idx} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-square">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {idx > 0 && (
                  <button type="button" onClick={() => moveImage(idx, idx - 1)} className="bg-white/20 hover:bg-white/30 text-white rounded w-6 h-6 text-xs">←</button>
                )}
                <button type="button" onClick={() => removeImage(idx)} className="bg-red-500/80 hover:bg-red-500 text-white rounded w-6 h-6 text-xs">✕</button>
                {idx < value.length - 1 && (
                  <button type="button" onClick={() => moveImage(idx, idx + 1)} className="bg-white/20 hover:bg-white/30 text-white rounded w-6 h-6 text-xs">→</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      <div
        className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-white/20 hover:border-white/40 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
      >
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
        {uploading ? (
          <span className="text-sm text-white/50">Uploading...</span>
        ) : (
          <>
            <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-sm text-white/50">Add images ({value.length} uploaded)</span>
          </>
        )}
      </div>
    </div>
  );
}


function SectionsEditor({ value = [], onChange, folder = "blog" }) {
  const addSection = () => {
    onChange([...value, { title: "", content: "", image: "" }]);
  };

  const updateSection = (idx, field, val) => {
    const updated = value.map((s, i) => i === idx ? { ...s, [field]: val } : s);
    onChange(updated);
  };

  const removeSection = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const moveSection = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const arr = [...value];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    onChange(arr);
  };

  return (
    <div className="space-y-3">
      {value.map((section, idx) => (
        <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-white/50">Section {idx + 1}</span>
            <div className="flex gap-1">
              {idx > 0 && (
                <button type="button" onClick={() => moveSection(idx, idx - 1)} className="text-white/40 hover:text-white text-xs px-1">↑</button>
              )}
              {idx < value.length - 1 && (
                <button type="button" onClick={() => moveSection(idx, idx + 1)} className="text-white/40 hover:text-white text-xs px-1">↓</button>
              )}
              <button type="button" onClick={() => removeSection(idx)} className="text-red-400 hover:text-red-300 text-xs px-1">✕</button>
            </div>
          </div>
          <Input
            value={section.title}
            onChange={(e) => updateSection(idx, "title", e.target.value)}
            placeholder="Section title (optional)"
            className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <Textarea
            value={section.content}
            onChange={(e) => updateSection(idx, "content", e.target.value)}
            placeholder="Section text..."
            rows={3}
            className="bg-black/20 border-white/10 text-white placeholder:text-white/30 text-sm"
          />
          <MediaImageField
            value={section.image || ""}
            onChange={(url) => updateSection(idx, "image", url)}
            folder={folder}
            aspectClass="h-28"
          />
        </div>
      ))}

      <button
        type="button"
        onClick={addSection}
        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-white/20 hover:border-white/40 text-white/50 hover:text-white/70 text-sm transition-colors"
      >
        + Add Section
      </button>
    </div>
  );
}
